import type PlayClient from '@empathybroker/tsclient-play'
import type { ActionTypeEnum } from '@empathybroker/tsclient-play/dist/enums/action-type.enum'
import type { Action } from '@empathybroker/tsclient-play/dist/models/action'
import type { RequestFilters as TsClientRequestFilters } from '@empathybroker/tsclient-play/dist/models/request-filters'
import type { Sort as PlaySort } from '@empathybroker/tsclient-play/dist/models/sort'
import type {
  AccessTokenProvider,
  CrudServiceListResponse,
  Filterable,
  Identifiable,
  Sortable,
} from '@empathyco/playboard-core-domain'
import type {
  PlayServiceCrudWrapper,
  PlayServiceCrudWrapperServiceListParams,
} from '../interfaces/crud'
import { EndpointEnum } from '@empathybroker/tsclient-play/dist/enums/endpoint.enum'
import { inject, injectable } from 'inversify'
import { TYPES } from '../../config/inversify.types'
import { ActiveFilterEnum, EnabledFilterEnum, FilterIdEnum } from '../../enums'

type RequestFilters = TsClientRequestFilters & {
  [FilterIdEnum.ACTIVE]?: boolean
  [FilterIdEnum.ENABLED]?: boolean
}

type FilterKey = string | FilterIdEnum.ACTIVE | FilterIdEnum.ENABLED
type FilterValue = Array<string> | boolean
type FilterParam = [FilterKey, FilterValue]

@injectable()
export class PlatformPlayServiceCrudWrapper<Dto extends Identifiable>
  implements PlayServiceCrudWrapper<Dto>
{
  private service: PlayClient
  public constructor(
    @inject(TYPES.AccessTokenProvider) protected getToken: AccessTokenProvider,
    @inject(TYPES.PlayClient) protected playService: () => PlayClient,
  ) {
    this.service = playService()
  }

  async list(
    feature: ActionTypeEnum,
    params: PlayServiceCrudWrapperServiceListParams,
  ): Promise<CrudServiceListResponse<Dto>> {
    const token = await this.getToken()

    const { query: filterQuery, ...filters } = (params.filters as any) ?? {}
    const query = params.search?.query ?? (filterQuery as unknown as string)
    try {
      const { data } = await this.service.list(
        token,
        feature,
        this.getFilters(filters),
        params.page,
        params.sort ? this.getSort(params.sort) : undefined,
        query,
        params.strictMatching,
        EndpointEnum.SEARCH,
        params.extraParameters,
      )

      if (!data) {
        throw new Error('Malformed api response')
      }

      return {
        results: data.items as unknown as Array<Dto>,
        numFound: data.total,
      }
    } catch (error) {
      throw error
    }
  }

  async create(feature: ActionTypeEnum, item: Dto): Promise<Dto> {
    const token = await this.getToken()

    try {
      const { data } = await this.service.create(
        token,
        feature,
        item as unknown as Action,
        undefined,
        EndpointEnum.SEARCH,
      )

      return data as unknown as Dto
    } catch (error) {
      throw error
    }
  }

  async read(feature: ActionTypeEnum, id: string): Promise<Dto> {
    const token = await this.getToken()

    try {
      const { data } = await this.service.read(token, feature, id, EndpointEnum.SEARCH)

      return data as unknown as Dto
    } catch (error) {
      throw error
    }
  }

  async update(feature: ActionTypeEnum, item: Dto): Promise<Dto> {
    const token = await this.getToken()

    try {
      const { data } = await this.service.update(
        token,
        feature,
        item as unknown as Action,
        undefined,
        EndpointEnum.SEARCH,
      )

      return data as unknown as Dto
    } catch (error) {
      throw error
    }
  }

  async delete(feature: ActionTypeEnum, id: string): Promise<void> {
    const token = await this.getToken()

    try {
      await this.service.delete(token, feature, id, EndpointEnum.SEARCH)
    } catch (error) {
      throw error
    }
  }

  private getSort(sort: Sortable['sort']): PlaySort {
    return {
      field: sort.field,
      direction: sort.direction?.toLowerCase() as PlaySort['direction'],
    }
  }

  private getFilters(filters: Filterable['filters']): RequestFilters {
    const hasValidValue = (filter: FilterParam): boolean => {
      const value = filter[1]

      return typeof value === 'boolean' || (value && value.length > 0)
    }

    const addFilterParamToRequestFilters = (
      requestFilters: RequestFilters,
      [filterKey, filterValue]: FilterParam,
    ): RequestFilters => {
      const filterExceptions: Record<string, string> = {
        [FilterIdEnum.ACTIVE]: ActiveFilterEnum.ACTIVE,
        [FilterIdEnum.ENABLED]: EnabledFilterEnum.ENABLED,
      }

      if (filterExceptions[filterKey]) {
        requestFilters[filterKey as FilterIdEnum.ACTIVE | FilterIdEnum.ENABLED] =
          typeof filterValue === 'boolean'
            ? filterValue
            : filterValue[0] === filterExceptions[filterKey]
      } else {
        requestFilters.filters ??= {}
        requestFilters.filters[filterKey] = filterValue
      }

      return requestFilters
    }

    return Object.entries(filters).filter(hasValidValue).reduce(addFilterParamToRequestFilters, {})
  }
}
